import { string, number, object } from 'prop-types';
import React, { createRef } from 'react';

import { rotateClassName, Wrapper } from './style';

export const easeInOutQuad = (t, b, c, d) => {
  t /= d / 2;
  if (t < 1) return (c / 2) * t * t + b;
  t--;
  return (-c / 2) * (t * (t - 2) - 1) + b;
};

class Spinner extends React.Component {

  constructor() {
    super();

    // is currently running
    this.running = false;

    // canvas from ref
    this.canvas = createRef();
    this.ctx = null;

    // calculations values
    this.startRadius = Math.PI * -0.5;
    this.endRadius = Math.PI * 2;

    this.endPerc = 100;
    this.interval = 1.5;
    this.secondCircDelay = 20;

    this.currentPercCirc1 = 0;
    this.currentPercCirc2 = 0;
  }

  componentDidMount() {
    this.run();
  }

  componentWillUnmount() {
    this.running = false;
  }

  run() {
    if (this.running) return;
    this.ctx = this.canvas.current.getContext('2d');
    this.running = true;
    this.animationInterval();
  }

  animationInterval = () => {
    // if stopped running, don't continue the loop
    if (!this.running) return;
    if (!this.ctx) {
      // canvas not ready, continue the loop but don't render yet
    } else {
      this.ctx.clearRect(0, 0, this.props.size, this.props.size);
      this.ctx.lineWidth = Math.floor(this.props.size * 0.075);
      this.ctx.strokeStyle = this.props.color;
      this.ctx.fillStyle = this.props.color;
      this.ctx.globalAlpha = 0.5;

      this.drawCircle(Math.min(Math.max(this.currentPercCirc1, 0), 100));
      this.drawCircle(Math.min(Math.max(this.currentPercCirc2 - this.secondCircDelay + 5, 0), 100));

      this.ctx.globalAlpha = 1;
      this.drawBall(Math.min(Math.max(this.currentPercCirc1, 0), 100));

      this.currentPercCirc1 += this.interval;
      if (this.currentPercCirc1 > this.endPerc + this.secondCircDelay) this.currentPercCirc1 = 0;

      this.currentPercCirc2 += this.interval;
      if (this.currentPercCirc2 > this.endPerc + this.secondCircDelay) this.currentPercCirc2 = 0;
    }
    requestAnimationFrame(this.animationInterval);
  };

  drawBall(perce) {
    this.ctx.beginPath();

    const anglePercent = easeInOutQuad(perce, 0, 1, this.endPerc);
    const angle = Math.PI * 1 + anglePercent * this.endRadius;

    const x = (-Math.sin(angle) * this.props.size) / 2.5;
    const y = (Math.cos(angle) * this.props.size) / 2.5;

    this.ctx.arc(this.props.size / 2 + x, this.props.size / 2 + y, this.props.size / 15, 0, Math.PI * 2, false);
    this.ctx.fill();
    this.ctx.closePath();
  }

  drawCircle(perce) {
    this.ctx.beginPath();
    // set rotating starting position
    const p = Math.max(0, perce - this.endPerc / 2);
    const startPercent = easeInOutQuad(p / 2, 0, 2, this.endPerc - this.endPerc / 2);
    const start = this.startRadius + startPercent * this.endRadius;

    const endPercent = easeInOutQuad(perce, 0, 1, this.endPerc);
    const end = this.startRadius + endPercent * this.endRadius;

    // arc(x, y, radius, startAngle, endAngle, anticlockwise)
    this.ctx.arc(this.props.size / 2, this.props.size / 2, this.props.size / 2.5, start, end, false);
    this.ctx.stroke();
    this.ctx.closePath();
  }

  render() {
    const { size, style, color, className } = this.props;
    /* eslint-disable max-len */
    return (
      <Wrapper style={{ width: size, height: size, ...style }} className={className}>
        <canvas ref={this.canvas} width={size} height={size} />
        <svg
          className={rotateClassName}
          style={{
            top: size / 2 - size / 6,
            left: size / 2 - size / 6,
            position: 'absolute',
          }}
          fill={color}
          width={size / 3}
          height={size / 3}
          viewBox="0 0 20 20"
        >
          <path
            d="M11.2,12.4L11,17.7c0.2,0.2,0.4,0.6,0.4,0.9c0,0.7-0.6,1.4-1.4,1.4c-0.7,0-1.4-0.6-1.4-1.4
c0-0.4,0.1-0.7,0.4-0.9l-0.2-5.3c-0.5-0.3-0.9-0.7-1.2-1.2L2.3,11c-0.2,0.2-0.6,0.4-0.9,0.4C0.6,11.4,0,10.7,0,10
c0-0.7,0.6-1.4,1.4-1.4C1.7,8.6,2,8.8,2.3,9l5.3-0.2c0.3-0.5,0.7-0.9,1.2-1.2L9,2.3C8.8,2,8.6,1.7,8.6,1.4C8.6,0.6,9.3,0,10,0
c0.7,0,1.4,0.6,1.4,1.4c0,0.4-0.1,0.7-0.4,0.9l0.2,5.3c0.5,0.3,0.9,0.7,1.2,1.2L17.7,9c0.2-0.2,0.6-0.4,0.9-0.4
c0.7,0,1.4,0.6,1.4,1.4c0,0.7-0.6,1.4-1.4,1.4c-0.4,0-0.7-0.1-0.9-0.4l-5.3,0.2C12.1,11.7,11.7,12.1,11.2,12.4z M10,11.4
c0.7,0,1.4-0.6,1.4-1.4c0-0.7-0.6-1.4-1.4-1.4c-0.7,0-1.4,0.6-1.4,1.4C8.6,10.7,9.3,11.4,10,11.4z"
          />
        </svg>
      </Wrapper>
    );
  }
  /* eslint-enable max-len */

}

Spinner.propTypes = {
  color: string,
  size: number,
  style: object,
  className: string,
};

Spinner.defaultProps = {
  color: '#FFF',
  size: 90,
};

export default Spinner;
